/**
 * 对话管理服务
 * 负责与后端Rust API通信，管理对话和消息的持久化存储
 */

import { APP_CONFIG } from '../config/version';
import { Platform } from 'react-native';

// 数据类型定义
export interface Message {
  id: string;
  conversation_id: string;
  content: string;
  is_user: boolean;
  message_type: 'chat' | 'system' | 'practice_hint';
  created_at: string;
  metadata?: string;
}

export interface Conversation {
  id: string;
  user_id: string;
  title: string;
  interface_style: 'game' | 'traditional';
  created_at: string;
  updated_at: string;
  is_active: boolean;
}

export interface ConversationSummary {
  id: string;
  user_id: string;
  title: string;
  interface_style: string;
  created_at: string;
  updated_at: string;
  is_active: boolean;
  message_count: number;
  user_message_count: number;
  last_message_at?: string;
  last_message?: string;
}

export interface ConversationWithMessages {
  conversation: Conversation;
  messages: Message[];
  total_messages: number;
  user_messages: number;
}

export interface CreateConversationRequest {
  title?: string;
  interface_style?: 'game' | 'traditional';
}

export interface CreateMessageRequest {
  conversation_id: string;
  content: string;
  is_user: boolean;
  message_type?: 'chat' | 'system' | 'practice_hint';
  metadata?: any;
}

export interface ChatWithAIRequest {
  conversation_id?: string;
  message: string;
  interface_style?: 'game' | 'traditional';
}

// API响应类型
interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

class ConversationService {
  private baseUrl: string;
  private token: string | null = null;

  constructor() {
    // 根据配置选择后端地址，支持Web和移动端
    if (APP_CONFIG.BACKEND_TARGET === 'development') {
      // Web使用localhost，移动端使用模拟器地址
      this.baseUrl = Platform.OS === 'web' ? 'http://localhost:3000/api' : 'http://10.0.2.2:3000/api';
    } else {
      this.baseUrl = 'https://clickmaster-api.onrender.com/api';
    }
  }

  // 设置认证token
  setAuthToken(token: string) {
    this.token = token;
  }

  // 清除认证token
  clearAuthToken() {
    this.token = null;
  }

  // 通用请求方法
  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;

    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (this.token) {
      headers.Authorization = `Bearer ${this.token}`;
    }

    // 添加超时控制
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000); // 15秒超时

    const config: RequestInit = {
      ...options,
      headers,
      signal: controller.signal,
      mode: 'cors',
      credentials: 'omit',
    };

    console.log(`🌐 ConversationService - ${options.method || 'GET'} ${url}`);

    try {
      const response = await fetch(url, config);

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result: ApiResponse<T> = await response.json();

      if (!result.success) {
        throw new Error(result.error || '请求失败');
      }

      return result.data as T;
    } catch (error) {
      clearTimeout(timeoutId);
      console.error('ConversationService request failed:', error);
      throw error;
    }
  }

  // 创建新对话
  async createConversation(request: CreateConversationRequest): Promise<Conversation> {
    return this.request<Conversation>('/conversations', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  // 获取用户对话列表
  async getConversations(): Promise<ConversationSummary[]> {
    return this.request<ConversationSummary[]>('/conversations');
  }

  // 获取对话详情和消息
  async getConversation(conversationId: string): Promise<ConversationWithMessages> {
    return this.request<ConversationWithMessages>(`/conversations/${conversationId}`);
  }

  // 添加消息
  async addMessage(request: CreateMessageRequest): Promise<Message> {
    return this.request<Message>('/messages', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  // 获取用户消息（用于练习生成）
  async getUserMessagesForPractice(
    conversationId?: string,
    limit: number = 50
  ): Promise<{ messages: Message[]; count: number }> {
    const params = new URLSearchParams();
    if (conversationId) {
      params.append('conversation_id', conversationId);
    }
    params.append('limit', limit.toString());

    return this.request<{ messages: Message[]; count: number }>(
      `/messages/practice?${params.toString()}`
    );
  }

  // 更新对话
  async updateConversation(
    conversationId: string,
    updates: { title?: string; interface_style?: 'game' | 'traditional' }
  ): Promise<Conversation> {
    return this.request<Conversation>(`/conversations/${conversationId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  // 删除对话
  async deleteConversation(conversationId: string): Promise<void> {
    await this.request<void>(`/conversations/${conversationId}`, {
      method: 'DELETE',
    });
  }

  // AI对话（一体化：保存用户消息 + AI回复 + 保存AI消息）
  async chatWithAI(request: ChatWithAIRequest): Promise<{
    conversation_id: string;
    user_message: Message;
    ai_reply: string;
    ai_message: Message;
  }> {
    return this.request<{
      conversation_id: string;
      user_message: Message;
      ai_reply: string;
      ai_message: Message;
    }>('/chat', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  // 辅助方法：转换消息格式（适配现有组件）
  convertToLocalMessage(message: Message): {
    id: string;
    text: string;
    isUser: boolean;
    timestamp: Date;
  } {
    return {
      id: message.id,
      text: message.content,
      isUser: message.is_user,
      timestamp: new Date(message.created_at),
    };
  }

  // 辅助方法：转换本地消息格式
  convertFromLocalMessage(
    localMessage: { id: string; text: string; isUser: boolean; timestamp: Date },
    conversationId: string
  ): CreateMessageRequest {
    return {
      conversation_id: conversationId,
      content: localMessage.text,
      is_user: localMessage.isUser,
      message_type: 'chat',
    };
  }

  // 批量同步本地消息到服务器
  async syncLocalMessages(
    conversationId: string,
    localMessages: Array<{ id: string; text: string; isUser: boolean; timestamp: Date }>
  ): Promise<Message[]> {
    const syncedMessages: Message[] = [];

    for (const localMsg of localMessages) {
      try {
        const request = this.convertFromLocalMessage(localMsg, conversationId);
        const syncedMsg = await this.addMessage(request);
        syncedMessages.push(syncedMsg);
      } catch (error) {
        console.error('Failed to sync message:', error);
        // 继续同步其他消息，不中断整个过程
      }
    }

    return syncedMessages;
  }
}

// 导出单例实例
export const conversationService = new ConversationService();
export default conversationService;
